import clsx from "clsx";
import { ReactElement } from "react";
import { FaDownload, FaExpandAlt } from "react-icons/fa";

import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

import { FilePreviewChip } from "./FilePreviewChip";
import { BotFileAttachment } from "./ToolConfig";
import { BotFileDownloadCard } from "./BotFileDownloadCard";

// Math
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

export interface ChatBotMessageProps {
  messageHtml: string;
  timeStamp: string;
  isBotResponse: boolean;
  attachments?: {
    fileName: string;
    fileSize: number;
    fileType: string;
    uploadID?: number;
    previewUrl?: string;
    downloadUrl?: string;
  }[];
  botAttachments?: BotFileAttachment[];
  isLoading?: boolean;
  loadingText?: string;
  onImageClick?: (url: string) => void;
  computerUseConfirm?: {
    schedule_tool_call_data: any;
    message: string;
  };
  sensitiveActionConfirm?: {
    schedule_tool_call_data: any;
    message: string;
  };
  recipientDisambiguation?: {
    search_name: string;
    candidates: Array<{name: string, email: string}>;
    interim_text: string;
  };
  errorMessage?: string;
  deepAgentCard?: {
    title: string;
    message: string;
    tone: 'info' | 'success' | 'warning' | 'danger';
    runId?: number;
    approvalId?: number;
    artifacts?: any[];
    outputPayload?: any;
  };
}

type GeneratedImageCardProps = {
  src: string;
  alt?: string;
  onImageClick?: (url: string) => void;
};

function getGeneratedImageFilename(src: string): string {
  try {
    const cleanPath = src.split("?")[0];
    const lastSegment = cleanPath.split("/").filter(Boolean).pop();
    if (lastSegment && /\.(webp|png|jpe?g)$/i.test(lastSegment)) {
      return lastSegment;
    }
  } catch {
    // Fall through to default filename.
  }
  return `generated-image-${Date.now()}.webp`;
}

async function downloadGeneratedImage(src: string): Promise<void> {
  const filename = getGeneratedImageFilename(src);

  try {
    const response = await fetch(src, {
      method: "GET",
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(`Image download failed with HTTP ${response.status}`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(objectUrl);
  } catch (error) {
    console.warn("Direct image download failed; opening image URL instead:", error);

    // Fallback: still gives the user a clean way to save the image if browser/CORS
    // rules prevent programmatic blob download after the Azure SAS redirect.
    window.open(src, "_blank", "noopener,noreferrer");
  }
}

function GeneratedImageCard({ src, alt, onImageClick }: GeneratedImageCardProps): ReactElement {
  const label = alt || "Generated image";

  return (
    <figure className="generated-image-card">
      <button
        type="button"
        className="generated-image-card__image-button"
        onClick={() => onImageClick?.(src)}
        aria-label="Open generated image preview"
      >
        <img
          src={src}
          alt={label}
          className="generated-image-card__image"
          loading="lazy"
        />
        <span className="generated-image-card__expand-badge" aria-hidden="true">
          <FaExpandAlt />
        </span>
      </button>

      <figcaption className="generated-image-card__actions">
        <span className="generated-image-card__label">{label}</span>
        <button
          type="button"
          className="generated-image-card__download-btn"
          onClick={() => void downloadGeneratedImage(src)}
          aria-label="Download generated image"
        >
          <FaDownload />
          <span>Download</span>
        </button>
      </figcaption>
    </figure>
  );
}

export function ChatBotMessage({
  messageHtml,
  timeStamp,
  isBotResponse,
  attachments,
  botAttachments,
  isLoading,
  loadingText,
  onImageClick,
  computerUseConfirm,
  sensitiveActionConfirm,
  recipientDisambiguation,
  errorMessage,
  deepAgentCard,
}: ChatBotMessageProps): ReactElement {
  const messageWrapperClasses = clsx(
    !isBotResponse && "justify-content-end",
    "d-flex",
    "align-items-baseline",
    "mb-2",
    "message-wrapper"
  );

  return (
    <div className={messageWrapperClasses}>
      <div style={{ maxWidth: "85%" }}>
        <div
          className={clsx(
            isBotResponse && "bg-secondary",
            !isBotResponse && "enlive-gradient",
            "card display-inline-block p-3 my-2 bg-inherit markdown-content"
          )}
        >
          {isLoading ? (
            // ── Loading state: show the real status text with shimmer animation ──
            <div className="loading-text-placeholder">
              {loadingText || "Thinking..."}
            </div>
          ) : (
            // ── Streaming / complete state: render Markdown ──
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeKatex]}
              components={{
                // Streaming cursor sentinel — render your CSS-themed blink span
                a({ node, href, children, ...props }: any) {
                  if (href === "#cursor") {
                    return <span className="cursor-blink" aria-hidden="true" />;
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                      {children}
                    </a>
                  );
                },
                // IMG Renderer
                img({ src, alt }: any) {
                  const imageSrc = typeof src === "string" ? src : "";

                  if (!imageSrc) {
                    return null;
                  }

                  return (
                    <GeneratedImageCard
                      src={imageSrc}
                      alt={typeof alt === "string" ? alt : "Generated image"}
                      onImageClick={onImageClick}
                    />
                  );
                },
                // Wrap tables in a horizontally-scrollable div for mobile
                table({ children }) {
                  return (
                    <div className="markdown-table-wrapper">
                      <table>{children}</table>
                    </div>
                  );
                },
                // Syntax-highlighted fenced code blocks; plain inline code unchanged
                code({ node, inline, className, children, ...props }: any) {
                  const match = /language-(\w+)/.exec(className || "");
                  
                  // 1. Real Code Blocks (has a language tag like ```python)
                  if (!inline && match) {
                    return (
                      <SyntaxHighlighter
                        style={oneDark}
                        language={match[1]}
                        PreTag="div"
                        customStyle={{
                          borderRadius: "0.5rem",
                          fontSize: "0.85em",
                          margin: "0.5em 0 0.75em 0",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        }}
                        {...props}
                      >
                        {String(children).replace(/\n$/, "")}
                      </SyntaxHighlighter>
                    );
                  }
                  
                  // 2. Untagged Fenced Blocks (like emails, IDs, or short text)
                  if (!inline && !match) {
                    return (
                      <div className="markdown-text-chip">
                        {String(children).replace(/\n$/, "")}
                      </div>
                    );
                  }
                  
                  // 3. Inline code — plain styled span
                  return (
                    <code className={className} {...props}>
                      {children}
                    </code>
                  );
                },
              }}
            >
              {messageHtml}
            </ReactMarkdown>
          )}
        </div>
        
        {/* Render Error Message */}
        {errorMessage && (
          <div className="error-message mt-2">
            {errorMessage}
          </div>
        )}

        {/* Render Deep Agent Card */}
        {deepAgentCard && (
          <div className={`deep-agent-card deep-agent-card--${deepAgentCard.tone} mt-2`}>
            <div className="deep-agent-card__header">
              <div className="deep-agent-card__title-wrap">
                <div className="deep-agent-card__eyebrow">
                  {deepAgentCard.tone === 'success' ? 'Completed' : 
                   deepAgentCard.tone === 'warning' ? 'Needs attention' : 
                   deepAgentCard.tone === 'danger' ? 'Action failed' : 'Deep Agent'}
                </div>
                <div className="deep-agent-card__title">{deepAgentCard.title}</div>
              </div>
            </div>
            <div className="deep-agent-card__message">{deepAgentCard.message}</div>
            
            {/* Artifacts */}
            {deepAgentCard.artifacts && deepAgentCard.artifacts.length > 0 && (
              <div className="deep-agent-card__section">
                <div className="deep-agent-card__section-title">Artifacts</div>
                <ul className="deep-agent-card__list">
                  {deepAgentCard.artifacts.map((artifact, idx) => (
                    <li key={idx}>
                      {artifact.storage_uri || artifact.url ? (
                        <a href={artifact.storage_uri || artifact.url} target="_blank" rel="noopener noreferrer">
                          {artifact.file_name || artifact.filename || 'artifact'}
                        </a>
                      ) : (
                        <span>{artifact.file_name || artifact.filename || 'artifact'}</span>
                      )}
                      <span className="deep-agent-card__meta"> ({artifact.mime_type || artifact.file_type || 'file'})</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Output Payload rendering removed to hide raw JSON from users */}

            {/* Approval Actions */}
            {deepAgentCard.runId && deepAgentCard.tone === 'warning' && (
              <div className="deep-agent-card__actions">
                <button 
                  className="deep-agent-action-btn deep-agent-action-btn--primary" 
                  onClick={() => (window as any).deepAgentApprovalAction(deepAgentCard.runId, deepAgentCard.approvalId ?? null, 'approve')}
                >
                  Approve action
                </button>
                <button 
                  className="deep-agent-action-btn deep-agent-action-btn--danger" 
                  onClick={() => (window as any).deepAgentApprovalAction(deepAgentCard.runId, deepAgentCard.approvalId ?? null, 'reject')}
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        )}

        {/* Render Recipient Disambiguation Picker */}
        {recipientDisambiguation && (
          <div className="recipient-picker-container mt-3">
            <p className="mb-2 fw-bold" style={{ color: '#1a4839' }}>
              Which email should I use for {recipientDisambiguation.search_name}?
            </p>
            <div className="recipient-picker-actions">
              {recipientDisambiguation.candidates.map((candidate, idx) => (
                <button
                  key={idx}
                  className="deep-agent-action-btn deep-agent-action-btn--secondary mb-2 w-100 text-start justify-content-start"
                  onClick={() => {
                    // We use a global window function to bridge back to ChatbotTool's sendChatBotMessage
                    if ((window as any).sendChatBotMessageGlobal) {
                      (window as any).sendChatBotMessageGlobal(candidate.email);
                    }
                  }}
                >
                  <span className="fw-bold me-2">{candidate.name}</span>
                  <span style={{ opacity: 0.8, fontWeight: 'normal' }}>{candidate.email}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Render Computer Use Confirmation Warning */}
        {computerUseConfirm && (
          <div className="computer-use-warning mt-3">
            <p><strong>⚠️ Warning</strong></p>
            <p>{computerUseConfirm.message}</p>
            <div className="computer-use-actions">
              <button 
                className="btn btn-success btn-sm me-2" 
                onClick={() => (window as any).confirmComputerUse(true, JSON.stringify(computerUseConfirm.schedule_tool_call_data))}
              >
                Yes, proceed
              </button>
              <button 
                className="btn btn-danger btn-sm" 
                onClick={() => (window as any).confirmComputerUse(false)}
              >
                No, cancel
              </button>
            </div>
          </div>
        )}

        {/* --- NEW: Render Sensitive Action Confirmation --- */}
        {sensitiveActionConfirm && (
          <div className="computer-use-warning mt-3" style={{ borderLeftColor: '#ffc107' }}>
            <p><strong>⚠️ Action Required</strong></p>
            <p>{sensitiveActionConfirm.message}</p>
            <div className="computer-use-actions">
              <button 
                className="btn btn-success btn-sm me-2" 
                onClick={() => (window as any).confirmSensitiveAction(true, encodeURIComponent(JSON.stringify(sensitiveActionConfirm.schedule_tool_call_data)))}
              >
                Confirm
              </button>
              <button 
                className="btn btn-danger btn-sm" 
                onClick={() => (window as any).confirmSensitiveAction(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Render sent attachment chips */}
        {attachments && attachments.length > 0 && (
          <div className="sent-attachment-container">
            {attachments.map((att, index) => (
              <div key={att.uploadID || index} className="sent-attachment-chip-container">
                <FilePreviewChip
                  fileName={att.fileName}
                  fileSize={att.fileSize}
                  fileType={att.fileType}
                  onRemove={() => {}}
                  isSentAttachment={true}
                  previewUrl={att.previewUrl}     // ← NEW: pass through the preview URL
                  downloadUrl={att.downloadUrl}   // ← NEW
                  onImageClick={onImageClick}     // ← NEW
                />
              </div>
            ))}
          </div>
        )}

        {/* Render bot-generated file download cards */}
        {botAttachments && botAttachments.length > 0 && (
          <div className="bot-attachment-container">
            {botAttachments.map((att) => (
              <BotFileDownloadCard
                key={att.fileId}
                fileName={att.fileName}
                fileSize={att.fileSize}
                fileType={att.fileType}
                description={att.description}
                downloadUrl={att.downloadUrl}
              />
            ))}
          </div>
        )}

        <div className={clsx(!isBotResponse && "text-end", "small px-2")}>
          {timeStamp}
        </div>
      </div>
    </div>
  );
}
